import _ from 'lodash';
import { isEmpty } from '../../../utils/object-utils';
import {
    getAnalyticsPagesRadarData,
    getAverageTimeSpentData,
    getAllAvailableTimes,
    getAllAvailableDistinctDates,
    getAnalyticsOverTimeData,
    getTotalAmountOfActiveVisitors,
    getTotalAmountOFVisitorsThatAcceptedCookies,
    getAnalyticsOverPageData
} from './response-data-utils';
import {
    getFormattedDate,
    getSmallFormattedDate,
    getFormattedNowDate,
    getFormattedSeconds,
    areDatesEqual,
    compareDates,
    getNowDate,
    getFormattedTime
} from '../../../utils/date-utils';
import {
    getDateRangeParameters,
    getDateToFilterByParameter,
    shortUrlFormat,
    getShowLatestData
} from './url-utils';
import { calculatePercentage } from '../../../utils/number-utils';

const analyticsDataInitialState = {
    analyticsCardTitle: getFormattedNowDate(),
    analyticsForPagesData: [],
    activeVisitors: 0,
    visitorsThatAcceptedCookies: 0,
    categoryName: '',
    visitorsThatRejectedCookies: 0,
    analyticsAverageTimeSpentData: [],
    totalAverageTimeForVisitors: getFormattedSeconds(0),
    averageTimeForVisitors: getFormattedSeconds(0)
};

export function formatAnalyticsData(responseData) {
    if (isEmpty(responseData)) {
        return analyticsDataInitialState;
    }

    const showLatestData = getShowLatestData();
    const dateToFilterBy = isEmpty(getDateToFilterByParameter()) ? getNowDate() : getDateToFilterByParameter();
    const dateRange = getDateRangeParameters();

    const sanitizedAnalytics = sanitizeResponseData(responseData);

    if (isEmpty(sanitizedAnalytics)) {
        return analyticsDataInitialState;
    }

    const filteredAnalytics = filterAnalyticsData(sanitizedAnalytics, { showLatestData, dateToFilterBy, dateRange });
    const averageTimeSpentData = getAverageTimeSpentData(filteredAnalytics.analyticsData);

    return {
        // Data for the analytic cards
        analyticsCardTitle: getAnalyticsCardTitle({ dateToFilterBy, dateRange }),
        activeVisitors: filteredAnalytics.activeVisitors,
        visitorsThatAcceptedCookies: filteredAnalytics.visitorsThatAcceptedCookies,
        visitorsThatRejectedCookies: filteredAnalytics.visitorsThatRejectedCookies,
        // Data for Average time spent
        analyticsAverageTimeSpentData: averageTimeSpentData.analyticsData,
        // Data for analytics over time
        analyticsOverTimeData: getAnalyticsOverTimeData(filteredAnalytics.analyticsData),
        // Data for each page visited
        analyticsForPagesData: filteredAnalytics.analyticsData,
        analyticsOverPageData: getAnalyticsOverPageData(filteredAnalytics.analyticsData),
        // Data for the radar,
        analyticsPagesRadarData: getAnalyticsPagesRadarData(filteredAnalytics.analyticsData),
        totalAverageTimeForVisitors: getFormattedSeconds(averageTimeSpentData.totalAverageTimeForVisitors),
        averageTimeForVisitors: getFormattedSeconds(averageTimeSpentData.averageOfAverageTimeForVisitors),
        allAvailableTimes: getAllAvailableTimes(filteredAnalytics.analyticsData),
        allAvailableDistinctDates: getAllAvailableDistinctDates(sanitizedAnalytics)
    };
}

function getAnalyticsCardTitle({ dateToFilterBy, dateRange }) {
    return isEmpty(dateRange) ?
        getFormattedDate(dateToFilterBy) :
        `${getSmallFormattedDate(dateRange.from)} - ${getSmallFormattedDate(dateRange.to)}`;
}

function sanitizeResponseData(responseData) {
    const copyOfResponseData = _.cloneDeep(responseData);

    const activeVisitors = getTotalAmountOfActiveVisitors(copyOfResponseData);

    return copyOfResponseData.map((responseAnalytic) => {
        const analyticDate = new Date(responseAnalytic.createdOnUTC);
        responseAnalytic.created = analyticDate;
        responseAnalytic.id = _.uniqueId();
        responseAnalytic.shortUrlFormat = shortUrlFormat(responseAnalytic.accessedURL);
        responseAnalytic.formattedCreatedDate = getFormattedDate(analyticDate);
        responseAnalytic.formattedCreatedTime = getFormattedTime(analyticDate);
        responseAnalytic.formattedAverageTimeForActiveVisitors = getFormattedSeconds(responseAnalytic.averageTimeForActiveVisitors);
        responseAnalytic.formattedAverageTimeForExpiredVisitors = getFormattedSeconds(responseAnalytic.averageTimeForExpiredVisitors);
        responseAnalytic.visitedPercentage = calculatePercentage(responseAnalytic.numberOfActiveVisitors, activeVisitors);
        responseAnalytic.numberOfRejectedCookies = responseAnalytic.numberOfActiveVisitors - responseAnalytic.numberOfAcceptedCookies;

        return responseAnalytic;
    }).sort((a, b) => {
        return compareDates(b.created, a.created);
    });
}

function filterAnalyticsData(analyticsData, { showLatestData, dateToFilterBy, dateRange }) {
    const isDateRangeEmpty = isEmpty(dateRange);
    const copyOfAnalyticsData = _.cloneDeep(analyticsData);

    if (!isEmpty(dateToFilterBy)) {
        dateToFilterBy.setHours(0, 0, 0, 0);
    }

    if (!isDateRangeEmpty) {
        dateRange.from.setHours(0, 0, 0, 0);
        dateRange.to.setHours(0, 0, 0, 0);
    }

    const filteredAnalytics = getFilterAnalyticsData(copyOfAnalyticsData, { showLatestData, dateToFilterBy, dateRange }).sort((a, b) => {
        return b.visitedPercentage - a.visitedPercentage;
    });

    const totalAmountOfActiveUsers = getTotalAmountOfActiveVisitors(filteredAnalytics);

    filteredAnalytics.forEach((analyticData) => {
        analyticData.visitedPercentage = calculatePercentage(analyticData.numberOfActiveVisitors, totalAmountOfActiveUsers);
    });

    const activeVisitors = getTotalAmountOfActiveVisitors(filteredAnalytics);
    const visitorsThatAcceptedCookies = getTotalAmountOFVisitorsThatAcceptedCookies(filteredAnalytics);

    return {
        analyticsData: filteredAnalytics,
        activeVisitors,
        visitorsThatAcceptedCookies,
        visitorsThatRejectedCookies: activeVisitors - visitorsThatAcceptedCookies
    };
}

function getFilterAnalyticsData(analyticsData, { showLatestData, dateToFilterBy, dateRange }) {

    if (showLatestData) {
        return analyticsData.filter((analytic) => analytic.isLatest === showLatestData);
    }

    const isDateRangeEmpty = isEmpty(dateRange);

    return analyticsData.filter((analytic) => {
        const copyOfAnalytic = _.cloneDeep(analytic);
        copyOfAnalytic.created.setHours(0, 0, 0, 0);
        if (isDateRangeEmpty) {
            return analytic.isLatest === false && areDatesEqual(copyOfAnalytic.created, dateToFilterBy);
        }
        const comparisonWithFromDateResult = compareDates(copyOfAnalytic.created, dateRange.from);
        const comparisonWithToDateResult = compareDates(copyOfAnalytic.created, dateRange.to);

        return analytic.isLatest === false &&
            (comparisonWithFromDateResult === 1 || comparisonWithFromDateResult === 0) &&
            (comparisonWithToDateResult === -1 || comparisonWithToDateResult === 0);
    });
}

export function formatDatesToFilter(datesToFilter) {
    if (!datesToFilter || datesToFilter.length <= 0) {
        return [];
    }
    return datesToFilter.map((dateToFilter, index) => {
        return {
            id: index,
            text: dateToFilter,
            value: new Date(dateToFilter)
        }
    })

}